// UserRolesTab — Custom RBAC P6: assign / revoke roles on a user.
// Spec: docs/specs/custom-rbac.md §6.4.
//
// Renders inside the existing /dashboard/users → user detail Drawer.
// Lists active role assignments (with scope), opens AssignRoleDrawer
// to grant new ones, and surfaces a combined permission preview.

import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Plus, ShieldCheck, Trash2, ServerCrash, RefreshCw, X } from "lucide-react";
import EmptyState from "./EmptyState.js";
import Drawer from "./Drawer.js";
import { Field } from "./Field.js";
import { useConfirmDialog } from "./ConfirmDialog.js";
import {
  getUserRoles,
  putUserRoles,
  listRoles,
  getRole,
  friendlyRolesError,
  type UserRoleAssignment,
  type RoleRow,
  type AssignmentInput,
} from "../lib/rolesApi.js";
import { listSites, type SiteRow } from "../lib/sitesApi.js";
import { listClients, type ClientRow } from "../lib/clientsApi.js";
import { listAllBuildings, type BuildingDto } from "../lib/floorStackApi.js";
import { PERMISSION_CATALOG, DOMAIN_LABELS, type PermissionMeta } from "../lib/permissionCatalog.js";

type State =
  | { status: "loading" }
  | { status: "ready"; items: UserRoleAssignment[] }
  | { status: "error"; message: string };

interface Props {
  userId: string | null;
  tenantId: string | null;
}

export default function UserRolesTab({ userId, tenantId }: Props): React.ReactElement {
  const [state, setState] = useState<State>({ status: "loading" });
  const [assignOpen, setAssignOpen] = useState(false);
  const dlg = useConfirmDialog();

  const load = useCallback(async () => {
    if (!userId) {
      setState({ status: "ready", items: [] });
      return;
    }
    setState({ status: "loading" });
    try {
      const r = await getUserRoles(userId, tenantId);
      setState({ status: "ready", items: r.items });
    } catch (err) {
      setState({ status: "error", message: friendlyRolesError(err) });
    }
  }, [userId, tenantId]);

  useEffect(() => { void load(); }, [load]);

  async function handleRevoke(assignment: UserRoleAssignment): Promise<void> {
    if (!userId || state.status !== "ready") return;
    const ok = await dlg.confirm(
      `確認移除此使用者的「${assignment.role_name}」角色？`,
      { destructive: true, okLabel: "移除" },
    );
    if (!ok) return;
    const remaining: AssignmentInput[] = state.items
      .filter((a) => a.id !== assignment.id)
      .map((a) => ({ role_id: a.role_id, scope_json: a.scope_json }));
    try {
      await putUserRoles(userId, remaining, tenantId);
      await load();
    } catch (err) {
      await dlg.alert(friendlyRolesError(err), { title: "移除失敗" });
    }
  }

  async function handleAdd(roleId: string, scope: AssignmentInput["scope_json"]): Promise<void> {
    if (!userId || state.status !== "ready") return;
    const next: AssignmentInput[] = [
      ...state.items.map((a) => ({ role_id: a.role_id, scope_json: a.scope_json })),
      { role_id: roleId, scope_json: scope },
    ];
    try {
      await putUserRoles(userId, next, tenantId);
      setAssignOpen(false);
      await load();
    } catch (err) {
      await dlg.alert(friendlyRolesError(err), { title: "指派失敗" });
    }
  }

  if (!userId) {
    return (
      <EmptyState
        icon={ShieldCheck}
        title="尚未綁定 LINE / 註冊"
        description="此成員仍為待邀請狀態，需先完成註冊才能指派自訂角色。"
      />
    );
  }

  if (state.status === "error") {
    return (
      <EmptyState
        icon={ServerCrash}
        title="無法載入角色"
        description={state.message}
        action={
          <button onClick={() => void load()} className="btn-secondary text-sm">
            <RefreshCw className="h-4 w-4" /> 重試
          </button>
        }
      />
    );
  }

  const items = state.status === "ready" ? state.items : [];

  return (
    <div className="space-y-4">
      {/* Assignment list */}
      <section>
        <div className="flex items-center justify-between mb-2">
          <h3 className="text-xs font-semibold uppercase tracking-wider text-text-muted">
            已指派角色（{items.length}）
          </h3>
          <button
            type="button"
            onClick={() => setAssignOpen(true)}
            className="btn-primary text-xs"
          >
            <Plus className="h-3.5 w-3.5" /> 指派角色
          </button>
        </div>
        {items.length === 0 ? (
          <div className="rounded-xl border border-dashed border-border bg-bg-surface p-6 text-center text-2xs text-text-muted">
            尚未指派任何角色
          </div>
        ) : (
          <div className="rounded-xl border border-border bg-bg-surface divide-y divide-border">
            {items.map((a) => (
              <AssignmentRowItem key={a.id} assignment={a} onRevoke={() => void handleRevoke(a)} />
            ))}
          </div>
        )}
      </section>

      {/* Effective permission preview */}
      {items.length > 0 && (
        <PermissionPreview assignments={items} tenantId={tenantId} />
      )}

      {/* Assign drawer */}
      <Drawer open={assignOpen} onClose={() => setAssignOpen(false)} title="指派角色" size="md">
        {assignOpen && (
          <AssignRoleForm
            currentRoleIds={new Set(items.map((a) => a.role_id))}
            tenantId={tenantId}
            onCancel={() => setAssignOpen(false)}
            onSubmit={(roleId, scope) => void handleAdd(roleId, scope)}
          />
        )}
      </Drawer>
    </div>
  );
}

function AssignmentRowItem({ assignment, onRevoke }: { assignment: UserRoleAssignment; onRevoke: () => void }): React.ReactElement {
  const scope = scopeLabel(assignment.scope_json);
  return (
    <div className="flex items-center justify-between gap-3 px-4 py-3">
      <div className="flex items-center gap-3 min-w-0">
        <span className={[
          "flex h-9 w-9 items-center justify-center rounded-lg flex-shrink-0",
          assignment.role_is_system ? "bg-accent-subtle text-accent" : "bg-bg-raised text-text-secondary border border-border",
        ].join(" ")}>
          <ShieldCheck className="h-4 w-4" />
        </span>
        <div className="min-w-0">
          <div className="text-sm font-medium text-text-primary truncate">
            {assignment.role_name}
            {assignment.role_is_system && (
              <span className="ml-2 text-2xs text-text-muted">（系統預設）</span>
            )}
          </div>
          <div className="text-2xs text-text-muted">{scope}</div>
          <div className="text-2xs text-text-muted mt-0.5">
            指派時間：<span className="font-mono">{formatTs(assignment.assigned_at)}</span>
            {" · "}指派者：<code className="font-mono">{assignment.assigned_by_user_id}</code>
          </div>
        </div>
      </div>
      <button
        type="button"
        onClick={onRevoke}
        className="btn-ghost text-xs text-danger flex-shrink-0"
        title="移除此角色"
      >
        <Trash2 className="h-3.5 w-3.5" /> 移除
      </button>
    </div>
  );
}

function AssignRoleForm({ currentRoleIds, tenantId, onCancel, onSubmit }: {
  currentRoleIds: ReadonlySet<string>;
  tenantId: string | null;
  onCancel: () => void;
  onSubmit: (roleId: string, scope: AssignmentInput["scope_json"]) => void;
}): React.ReactElement {
  const [allRoles, setAllRoles] = useState<RoleRow[] | null>(null);
  const [sites, setSites] = useState<SiteRow[] | null>(null);
  const [clients, setClients] = useState<ClientRow[] | null>(null);
  const [buildings, setBuildings] = useState<BuildingDto[] | null>(null);
  const [selectedRoleId, setSelectedRoleId] = useState<string>("");
  const [scopeMode, setScopeMode] = useState<"tenant" | "sites" | "buildings" | "clients">("tenant");
  const [selectedSites, setSelectedSites] = useState<Set<string>>(new Set());
  const [selectedBuildings, setSelectedBuildings] = useState<Set<string>>(new Set());
  const [selectedClients, setSelectedClients] = useState<Set<string>>(new Set());
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const [rolesR, sitesR, clientsR, buildingsR] = await Promise.all([
          listRoles(tenantId),
          listSites({ tenantId, limit: 200 }),
          listClients({ tenantId, limit: 200 }),
          listAllBuildings(tenantId).catch(() => [] as BuildingDto[]),
        ]);
        if (!alive) return;
        setAllRoles(rolesR.items);
        setSites(sitesR.items);
        setClients(clientsR.items);
        setBuildings(buildingsR);
      } catch {
        if (alive) {
          setAllRoles([]);
          setSites([]);
          setClients([]);
          setBuildings([]);
        }
      }
    })();
    return () => { alive = false; };
  }, [tenantId]);

  const availableRoles = useMemo(() => {
    if (!allRoles) return [];
    return allRoles.filter((r) => !currentRoleIds.has(r.id) && !r.archived_at);
  }, [allRoles, currentRoleIds]);

  function handleSubmit(): void {
    if (!selectedRoleId) return;
    setSubmitting(true);
    let scope: AssignmentInput["scope_json"] = null;
    if (scopeMode === "sites" && selectedSites.size > 0) {
      scope = { sites: [...selectedSites] };
    } else if (scopeMode === "buildings" && selectedBuildings.size > 0) {
      scope = { buildings: [...selectedBuildings] };
    } else if (scopeMode === "clients" && selectedClients.size > 0) {
      scope = { clients: [...selectedClients] };
    }
    onSubmit(selectedRoleId, scope);
  }

  if (!allRoles || !sites || !clients || !buildings) {
    return <div className="text-2xs text-text-muted py-8 text-center">載入角色列表…</div>;
  }

  return (
    <div className="space-y-4">
      <Field label="角色 *">
        <select
          className="select"
          value={selectedRoleId}
          onChange={(e) => setSelectedRoleId(e.target.value)}
        >
          <option value="">— 選擇角色 —</option>
          {availableRoles.length === 0 && (
            <option disabled>（沒有可指派的新角色）</option>
          )}
          {availableRoles.map((r) => (
            <option key={r.id} value={r.id}>
              {r.name}{r.is_system ? "（系統預設）" : ""} · {r.permission_count} 個權限
            </option>
          ))}
        </select>
      </Field>

      <Field label="範圍">
        <div className="space-y-2">
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="radio"
              name="scope"
              value="tenant"
              checked={scopeMode === "tenant"}
              onChange={() => setScopeMode("tenant")}
              className="border-border bg-bg-base text-accent focus:ring-accent focus:ring-offset-0"
            />
            <span className="text-sm text-text-primary">整個單位（tenant-wide）</span>
          </label>
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="radio"
              name="scope"
              value="sites"
              checked={scopeMode === "sites"}
              onChange={() => setScopeMode("sites")}
              className="border-border bg-bg-base text-accent focus:ring-accent focus:ring-offset-0"
            />
            <span className="text-sm text-text-primary">限定特定案場</span>
          </label>
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="radio"
              name="scope"
              value="buildings"
              checked={scopeMode === "buildings"}
              onChange={() => setScopeMode("buildings")}
              className="border-border bg-bg-base text-accent focus:ring-accent focus:ring-offset-0"
            />
            <span className="text-sm text-text-primary">限定特定樓棟</span>
          </label>
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="radio"
              name="scope"
              value="clients"
              checked={scopeMode === "clients"}
              onChange={() => setScopeMode("clients")}
              className="border-border bg-bg-base text-accent focus:ring-accent focus:ring-offset-0"
            />
            <span className="text-sm text-text-primary">限定特定終端客戶</span>
          </label>
        </div>
      </Field>

      {scopeMode === "sites" && (
        <Field label="案場（可複選）">
          {sites.length === 0 ? (
            <div className="text-2xs text-text-muted">尚無案場可選</div>
          ) : (
            <div className="max-h-64 overflow-y-auto rounded-md border border-border bg-bg-base divide-y divide-border">
              {sites.map((s) => {
                const checked = selectedSites.has(s.id);
                return (
                  <label key={s.id} className="flex items-center gap-2 px-3 py-2 cursor-pointer hover:bg-bg-hover">
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={() => {
                        const next = new Set(selectedSites);
                        if (next.has(s.id)) next.delete(s.id);
                        else next.add(s.id);
                        setSelectedSites(next);
                      }}
                      className="rounded border-border bg-bg-base text-accent focus:ring-accent focus:ring-offset-0"
                    />
                    <span className="text-sm text-text-primary truncate">{s.name}</span>
                    {s.client_name && <span className="text-2xs text-text-muted truncate">· {s.client_name}</span>}
                  </label>
                );
              })}
            </div>
          )}
          <div className="mt-1 text-2xs text-text-muted">
            已選 {selectedSites.size} 個案場
          </div>
        </Field>
      )}

      {scopeMode === "buildings" && (
        <Field label="樓棟（可複選，依案場分組）">
          {buildings.length === 0 ? (
            <div className="text-2xs text-text-muted">尚無樓棟可選</div>
          ) : (
            <div className="max-h-64 overflow-y-auto rounded-md border border-border bg-bg-base divide-y divide-border">
              {(() => {
                // Group buildings by site for clearer picking — flat list
                // is confusing when names collide across sites.
                const siteNameById = new Map(sites.map((s) => [s.id, s.name]));
                const groups = new Map<string, BuildingDto[]>();
                for (const b of buildings) {
                  const arr = groups.get(b.site_id) ?? [];
                  arr.push(b);
                  groups.set(b.site_id, arr);
                }
                const out: React.ReactElement[] = [];
                for (const [siteId, bs] of groups.entries()) {
                  out.push(
                    <div key={`hdr_${siteId}`} className="px-3 py-1 bg-bg-surface text-2xs text-text-muted">
                      {siteNameById.get(siteId) ?? siteId}
                    </div>,
                  );
                  for (const b of bs) {
                    const checked = selectedBuildings.has(b.id);
                    out.push(
                      <label key={b.id} className="flex items-center gap-2 px-3 py-2 cursor-pointer hover:bg-bg-hover">
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={() => {
                            const next = new Set(selectedBuildings);
                            if (next.has(b.id)) next.delete(b.id);
                            else next.add(b.id);
                            setSelectedBuildings(next);
                          }}
                          className="rounded border-border bg-bg-base text-accent focus:ring-accent focus:ring-offset-0"
                        />
                        <span className="text-sm text-text-primary truncate">{b.name}</span>
                      </label>,
                    );
                  }
                }
                return out;
              })()}
            </div>
          )}
          <div className="mt-1 text-2xs text-text-muted">
            已選 {selectedBuildings.size} 個樓棟
          </div>
        </Field>
      )}

      {scopeMode === "clients" && (
        <Field label="終端客戶（可複選）">
          {clients.length === 0 ? (
            <div className="text-2xs text-text-muted">尚無客戶可選</div>
          ) : (
            <div className="max-h-64 overflow-y-auto rounded-md border border-border bg-bg-base divide-y divide-border">
              {clients.map((cl) => {
                const checked = selectedClients.has(cl.id);
                return (
                  <label key={cl.id} className="flex items-center gap-2 px-3 py-2 cursor-pointer hover:bg-bg-hover">
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={() => {
                        const next = new Set(selectedClients);
                        if (next.has(cl.id)) next.delete(cl.id);
                        else next.add(cl.id);
                        setSelectedClients(next);
                      }}
                      className="rounded border-border bg-bg-base text-accent focus:ring-accent focus:ring-offset-0"
                    />
                    <span className="text-sm text-text-primary truncate">{cl.name}</span>
                  </label>
                );
              })}
            </div>
          )}
          <div className="mt-1 text-2xs text-text-muted">
            已選 {selectedClients.size} 個客戶
          </div>
        </Field>
      )}

      <div className="flex items-center justify-end gap-2 pt-2 border-t border-border">
        <button type="button" onClick={onCancel} className="btn-secondary text-sm" disabled={submitting}>
          <X className="h-4 w-4" /> 取消
        </button>
        <button
          type="button"
          onClick={handleSubmit}
          disabled={!selectedRoleId || submitting}
          className="btn-primary text-sm"
        >
          <Plus className="h-4 w-4" /> {submitting ? "指派中…" : "指派"}
        </button>
      </div>
    </div>
  );
}

function PermissionPreview({ assignments, tenantId }: { assignments: UserRoleAssignment[]; tenantId: string | null }): React.ReactElement {
  const [perms, setPerms] = useState<Set<string> | null>(null);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const details = await Promise.all(
          assignments.map((a) => getRole(a.role_id, tenantId).catch(() => null)),
        );
        if (!alive) return;
        const set = new Set<string>();
        for (const d of details) {
          if (!d) continue;
          for (const p of d.permissions) set.add(p);
        }
        setPerms(set);
      } catch {
        if (alive) setPerms(new Set());
      }
    })();
    return () => { alive = false; };
  }, [assignments, tenantId]);

  const byDomain: { domain: string; selected: number; total: number }[] = useMemo(() => {
    const total = new Map<string, number>();
    const selected = new Map<string, number>();
    for (const p of PERMISSION_CATALOG) {
      total.set(p.domain, (total.get(p.domain) ?? 0) + 1);
      if (perms?.has(p.id)) selected.set(p.domain, (selected.get(p.domain) ?? 0) + 1);
    }
    const order: string[] = [];
    const seen = new Set<string>();
    for (const p of PERMISSION_CATALOG as ReadonlyArray<PermissionMeta>) {
      if (!seen.has(p.domain)) { seen.add(p.domain); order.push(p.domain); }
    }
    return order
      .map((d) => ({ domain: d, selected: selected.get(d) ?? 0, total: total.get(d) ?? 0 }))
      .filter((x) => x.selected > 0);
  }, [perms]);

  return (
    <section>
      <h3 className="text-xs font-semibold uppercase tracking-wider text-text-muted mb-2">
        有效權限預覽（{perms?.size ?? "…"} 個權限）
      </h3>
      {!perms ? (
        <div className="rounded-xl border border-border bg-bg-surface p-4 text-2xs text-text-muted">
          計算中…
        </div>
      ) : byDomain.length === 0 ? (
        <div className="rounded-xl border border-border bg-bg-surface p-4 text-2xs text-text-muted">
          此使用者目前沒有任何有效權限
        </div>
      ) : (
        <div className="rounded-xl border border-border bg-bg-surface p-3 grid grid-cols-2 sm:grid-cols-3 gap-2">
          {byDomain.map((d) => (
            <div key={d.domain} className="flex items-center justify-between text-2xs px-2 py-1 rounded bg-bg-base">
              <span className="text-text-primary truncate">{DOMAIN_LABELS[d.domain] ?? d.domain}</span>
              <span className={[
                "font-mono",
                d.selected === d.total ? "text-accent" : "text-text-secondary",
              ].join(" ")}>
                {d.selected} / {d.total}
              </span>
            </div>
          ))}
        </div>
      )}
      <div className="mt-1 text-2xs text-text-muted">
        ※ Phase 1 預覽：以角色內定義的權限聯集計算，尚未套用 scope 限制。完整 scope-aware 權限解析需待 P9。
      </div>
    </section>
  );
}

function scopeLabel(scope: UserRoleAssignment["scope_json"]): string {
  if (!scope) return "整個單位（tenant-wide）";
  const parts: string[] = [];
  if (scope.sites?.length) parts.push(`案場 ${scope.sites.length} 個`);
  if (scope.buildings?.length) parts.push(`樓棟 ${scope.buildings.length} 個`);
  if (scope.clients?.length) parts.push(`客戶 ${scope.clients.length} 個`);
  return parts.length === 0 ? "整個單位（tenant-wide）" : `限定：${parts.join("、")}`;
}

function formatTs(ts: number): string {
  try {
    return new Date(ts).toISOString().replace("T", " ").slice(0, 16) + "Z";
  } catch {
    return String(ts);
  }
}
